import { useCallback, useEffect, useState } from 'react';
import { getProviderNameForBillRecord } from '@demandproai/shared';
import { validateMedicalBillProviderName } from '../utils/medicalBillValidation';

// The rename is held locally and only handed to the parent once it validates, so a
// half-typed name never reaches the debounced save and never collides with another row.
const useMedicalBillProviderNameCell = ({ records, rowIndex, onCommit }) => {
  const savedProviderName = getProviderNameForBillRecord(records?.[rowIndex]);
  const [draftProviderName, setDraftProviderName] = useState(savedProviderName);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    setDraftProviderName(savedProviderName);
    setValidationError('');
  }, [savedProviderName]);

  const revertDraft = useCallback(() => {
    setDraftProviderName(savedProviderName);
    setValidationError('');
  }, [savedProviderName]);

  const handleChange = useCallback(
    (event) => {
      const nextName = event.target.value;
      setDraftProviderName(nextName);
      const { isValid, error } = validateMedicalBillProviderName({
        value: nextName,
        records,
        rowIndex,
      });
      setValidationError(isValid ? '' : error);
    },
    [records, rowIndex]
  );

  const handleBlur = useCallback(() => {
    const { isValid, value } = validateMedicalBillProviderName({
      value: draftProviderName,
      records,
      rowIndex,
    });

    if (!isValid) {
      revertDraft();
      return;
    }

    setDraftProviderName(value);
    setValidationError('');
    if (value !== savedProviderName) onCommit?.(value);
  }, [draftProviderName, records, rowIndex, savedProviderName, onCommit, revertDraft]);

  const handleKeyDown = useCallback(
    (event) => {
      // stays focused on Escape: blurring here would run the commit against the
      // pre-revert draft still captured by this render's handleBlur
      if (event.key === 'Escape') {
        revertDraft();
        return;
      }
      // the textarea already swallows Enter to stay single-line; reuse it as "commit"
      if (event.key === 'Enter') event.currentTarget.blur();
    },
    [revertDraft]
  );

  return { draftProviderName, validationError, handleChange, handleBlur, handleKeyDown };
};

export default useMedicalBillProviderNameCell;
